import React, { useCallback, useEffect, useMemo, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useBranch } from "@/context/BranchContext";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Calendar, RefreshCw, Loader2, Send, Clock, AlertTriangle,
  BookmarkPlus, Trash2, Pencil, Users,
} from "lucide-react";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const ROTA_DAYS_FULL = [
  { k: "saturday", en: "Saturday", ar: "السبت" },
  { k: "sunday", en: "Sunday", ar: "الأحد" },
  { k: "monday", en: "Monday", ar: "الإثنين" },
  { k: "tuesday", en: "Tuesday", ar: "الثلاثاء" },
  { k: "wednesday", en: "Wednesday", ar: "الأربعاء" },
  { k: "thursday", en: "Thursday", ar: "الخميس" },
  { k: "friday", en: "Friday", ar: "الجمعة" },
];

const clipTime = (t) => (t || "10:00").slice(0, 5);

const normalizeWeeklyRotaPayload = (wr, branchId = "") => {
  const out = {};
  ROTA_DAYS_FULL.forEach((d) => {
    const prev = wr?.[d.k] || {};
    out[d.k] = {
      branch_id: branchId || "",
      off: false,
      ...prev,
      start: clipTime(prev.start || "10:00"),
      end: clipTime(prev.end || "19:00"),
    };
  });
  return out;
};

const countWorkingDays = (wr) => Object.values(wr || {}).filter((d) => d && !d.off).length;

function RotaWeeklyDayGrid({ rota, setDay, isAr, branchId }) {
  return (
    <div className="space-y-2">
      {ROTA_DAYS_FULL.map((d) => {
        const cfg = rota[d.k] || { off: false, start: "10:00", end: "19:00", branch_id: branchId || "" };
        return (
          <div key={d.k} className="flex flex-wrap items-center gap-2 text-sm border-b border-[var(--border-subtle)] pb-2">
            <span className="w-24 text-[var(--text-muted)]">{isAr ? d.ar : d.en}</span>
            <label className="flex items-center gap-1 text-xs">
              <input type="checkbox" checked={!!cfg.off} onChange={(e) => setDay(d.k, { off: e.target.checked })}/>
              {isAr ? "إجازة" : "Off"}
            </label>
            {!cfg.off && (
              <>
                <input type="time" value={cfg.start || "10:00"} onChange={(e) => setDay(d.k, { start: e.target.value })} className="px-2 py-1 border rounded text-xs"/>
                <span>→</span>
                <input type="time" value={cfg.end || "19:00"} onChange={(e) => setDay(d.k, { end: e.target.value })} className="px-2 py-1 border rounded text-xs"/>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RotaTemplatePanel({ employees, branchId, isAr, t, onApplied }) {
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formName, setFormName] = useState("");
  const [formRota, setFormRota] = useState(() => normalizeWeeklyRotaPayload({}, branchId));
  const [applyIds, setApplyIds] = useState([]);
  const [onlyNoTemplate, setOnlyNoTemplate] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/rota-templates");
      setTemplates(r.data?.templates || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const openCreate = () => {
    setFormName("");
    setFormRota(normalizeWeeklyRotaPayload({}, branchId));
    setModal({ mode: "create" });
  };

  const openEdit = (tpl) => {
    setFormName(isAr && tpl.name_ar ? tpl.name_ar : tpl.name);
    setFormRota(normalizeWeeklyRotaPayload(tpl.weekly_rota || {}, branchId || tpl.branch_id));
    setModal({ mode: "edit", template_id: tpl.template_id });
  };

  const openApply = (tpl) => {
    setOnlyNoTemplate(false);
    setApplyIds((employees || []).map((e) => e.employee_id));
    setModal({ mode: "apply", template: tpl });
  };

  const setDay = (key, patch) => {
    setFormRota((prev) => ({
      ...prev,
      [key]: { off: false, start: "10:00", end: "19:00", branch_id: branchId || "", ...(prev[key] || {}), ...patch },
    }));
  };

  const saveTemplate = async () => {
    const name = formName.trim();
    if (!name) {
      toast.error(isAr ? "اسم القالب مطلوب" : "Template name is required");
      return;
    }
    const weekly_rota = normalizeWeeklyRotaPayload(formRota, branchId);
    if (countWorkingDays(weekly_rota) === 0) {
      toast.error(isAr ? "يجب أن يكون يوم عمل واحد على الأقل غير محدد كإجازة" : "At least one day must not be marked Off");
      return;
    }
    setSaving(true);
    try {
      const payload = { name, weekly_rota, branch_id: branchId || null };
      if (modal.mode === "edit") {
        await api.put(`/rota-templates/${modal.template_id}`, payload);
      } else {
        await api.post("/rota-templates", payload);
      }
      toast.success(t("rota_template_created"));
      setModal(null);
      loadTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (tpl) => {
    const label = isAr && tpl.name_ar ? tpl.name_ar : tpl.name;
    const ok = await confirm({
      title: isAr ? `حذف القالب «${label}»؟` : `Delete template "${label}"?`,
      confirmLabel: isAr ? "حذف" : "Delete",
      cancelLabel: isAr ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/rota-templates/${tpl.template_id}`);
      toast.success(t("rota_template_deleted"));
      loadTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const applyTemplate = async () => {
    if (!modal?.template || applyIds.length === 0) return;
    setSaving(true);
    try {
      await api.post(`/rota-templates/${modal.template.template_id}/apply`, {
        employee_ids: applyIds,
        branch_id: branchId || modal.template.branch_id || null,
      });
      toast.success(t("rota_template_applied"));
      setModal(null);
      window.dispatchEvent(new CustomEvent("isaloon-rota-template-saved"));
      onApplied?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const applyEmployees = (employees || []).filter((e) => !onlyNoTemplate || !e.has_weekly_rota);

  const toggleApplyId = (id) => {
    setApplyIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <div className="card-surface p-4 space-y-3" data-testid="rota-template-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg gold-text">{t("rota_template_library")}</h2>
          <p className="text-xs text-[var(--text-muted)] mt-1 max-w-2xl">{t("rota_template_library_help")}</p>
        </div>
        <button type="button" onClick={openCreate} className="btn-gold px-3 py-2 rounded-md text-sm flex items-center gap-2" data-testid="rota-template-new">
          <BookmarkPlus size={14}/>{t("rota_template_new")}
        </button>
      </div>
      {loading ? (
        <div className="text-sm text-[var(--text-muted)] py-2">...</div>
      ) : templates.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">{t("rota_template_no_templates")}</p>
      ) : (
        <div className="space-y-2">
          {templates.map((tpl) => {
            const label = isAr && tpl.name_ar ? tpl.name_ar : tpl.name;
            const days = countWorkingDays(tpl.weekly_rota);
            return (
              <div key={tpl.template_id} className="flex flex-wrap items-center justify-between gap-2 border border-[var(--border-subtle)] rounded-md px-3 py-2 text-sm">
                <div>
                  <div className="font-medium">{label}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">
                    {days} {isAr ? "أيام عمل" : "working days"}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => openApply(tpl)} className="btn-gold px-3 py-1.5 rounded text-xs flex items-center gap-1" data-testid={`apply-rota-template-${tpl.template_id}`}>
                    <Users size={12}/>{t("rota_template_apply")}
                  </button>
                  <button type="button" onClick={() => openEdit(tpl)} className="px-3 py-1.5 rounded border text-xs flex items-center gap-1">
                    <Pencil size={12}/>{isAr ? "تعديل" : "Edit"}
                  </button>
                  <button type="button" onClick={() => deleteTemplate(tpl)} className="px-3 py-1.5 rounded border border-rose-200 text-rose-700 text-xs flex items-center gap-1">
                    <Trash2 size={12}/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (modal.mode === "create" || modal.mode === "edit") && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setModal(null))}>
          <div className="card-surface p-5 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto" onMouseDown={onModalPanelMouseDown}>
            <h3 className="font-heading text-lg gold-text">
              {modal.mode === "edit" ? (isAr ? "تعديل القالب" : "Edit template") : t("rota_template_new")}
            </h3>
            <div>
              <label className="text-xs text-[var(--text-muted)]">{t("rota_template_name")}</label>
              <input className="input-dark w-full" value={formName} onChange={(e) => setFormName(e.target.value)} data-testid="rota-template-name"/>
            </div>
            <RotaWeeklyDayGrid rota={formRota} setDay={setDay} isAr={isAr} branchId={branchId}/>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setModal(null)} className="px-4 py-2 rounded border">{t("cancel")}</button>
              <button type="button" onClick={saveTemplate} disabled={saving} className="btn-gold px-4 py-2 rounded-md" data-testid="save-rota-template">
                {saving ? "..." : (isAr ? "حفظ" : "Save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal?.mode === "apply" && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setModal(null))}>
          <div className="card-surface p-5 w-full max-w-md space-y-4 max-h-[90vh] overflow-y-auto" onMouseDown={onModalPanelMouseDown}>
            <h3 className="font-heading text-lg gold-text">{t("rota_template_apply_to")}</h3>
            <p className="text-sm text-[var(--text-muted)]">
              {isAr && modal.template.name_ar ? modal.template.name_ar : modal.template.name}
            </p>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={onlyNoTemplate} onChange={(e) => {
                setOnlyNoTemplate(e.target.checked);
                const next = (employees || []).filter((emp) => !e.target.checked || !emp.has_weekly_rota);
                setApplyIds(next.map((emp) => emp.employee_id));
              }}/>
              {t("rota_template_only_no_template")}
            </label>
            <div className="flex justify-between items-center text-xs">
              <span className="text-[var(--text-muted)]">{applyIds.length} {isAr ? "محدد" : "selected"}</span>
              <button
                type="button"
                className="text-[var(--gold)] underline"
                onClick={() => setApplyIds(applyEmployees.map((e) => e.employee_id))}
              >
                {t("rota_template_select_all")}
              </button>
            </div>
            <div className="max-h-56 overflow-y-auto space-y-1 border border-[var(--border-subtle)] rounded-md p-2">
              {applyEmployees.map((emp) => (
                <label key={emp.employee_id} className="flex items-center gap-2 text-sm py-1">
                  <input
                    type="checkbox"
                    checked={applyIds.includes(emp.employee_id)}
                    onChange={() => toggleApplyId(emp.employee_id)}
                  />
                  <span>{emp.employee_name}</span>
                  {!emp.has_weekly_rota && (
                    <span className="text-[10px] text-amber-600">{isAr ? "بدون قالب" : "No template"}</span>
                  )}
                </label>
              ))}
              {applyEmployees.length === 0 && (
                <p className="text-sm text-[var(--text-muted)] py-2">{isAr ? "لا يوجد موظفون" : "No employees"}</p>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setModal(null)} className="px-4 py-2 rounded border">{t("cancel")}</button>
              <button type="button" onClick={applyTemplate} disabled={saving || applyIds.length === 0} className="btn-gold px-4 py-2 rounded-md" data-testid="confirm-apply-rota-template">
                {saving ? "..." : t("rota_template_apply")}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}

const ROTA_DAYS = [
  { k: "saturday", en: "Sat", ar: "سب" },
  { k: "sunday", en: "Sun", ar: "أحد" },
  { k: "monday", en: "Mon", ar: "إثن" },
  { k: "tuesday", en: "Tue", ar: "ثلا" },
  { k: "wednesday", en: "Wed", ar: "أرب" },
  { k: "thursday", en: "Thu", ar: "خمي" },
  { k: "friday", en: "Fri", ar: "جمع" },
];

const weekStartSaturday = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = dt.getUTCDay();
  const diff = (dow + 1) % 7;
  dt.setUTCDate(dt.getUTCDate() - diff);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
};

const addDays = (iso, n) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
};

const fmtCell = (shift) => {
  if (!shift) return "—";
  if (shift.override_type === "off") return "OFF";
  return `${shift.start || ""}–${shift.end || ""}`;
};

const cellSourceLabel = (shift, isAr) => {
  if (!shift) return "";
  if (shift.source === "override") {
    if (shift.override_type === "emergency_cover") return isAr ? "طارئ" : "Cover";
    if (shift.override_type === "extra") return isAr ? "إضافي" : "Extra";
    return isAr ? "تعديل" : "Override";
  }
  return isAr ? "قالب" : "Template";
};

const cellTone = (shift) => {
  if (!shift) return "bg-stone-50 text-stone-400";
  if (shift.override_type === "off") return "bg-rose-50 text-rose-700";
  if (shift.source === "override") {
    if (shift.override_type === "emergency_cover") return "bg-amber-50 text-amber-900 border-amber-200";
    if (shift.override_type === "extra") return "bg-sky-50 text-sky-900";
    return "bg-violet-50 text-violet-900";
  }
  return "bg-emerald-50/70 text-emerald-900";
};

export default function StaffRota() {
  const { t, lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const isAr = lang === "ar";
  const { activeBranchId, branches } = useBranch();
  const [weekStart, setWeekStart] = useState(() => weekStartSaturday(new Date().toISOString().slice(0, 10)));
  const [branchScope, setBranchScope] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);

  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);

  useEffect(() => {
    if (!branchScope && activeBranchId) setBranchScope(activeBranchId);
  }, [activeBranchId, branchScope]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ start: weekStart, end: weekEnd });
      if (branchScope && branchScope !== "all") q.set("branch", branchScope);
      const r = await api.get(`/staff-rota/week?${q}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  }, [weekStart, weekEnd, branchScope]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onTemplateSaved = () => load();
    window.addEventListener("isaloon-rota-template-saved", onTemplateSaved);
    return () => window.removeEventListener("isaloon-rota-template-saved", onTemplateSaved);
  }, [load]);

  const publish = async () => {
    try {
      await api.post("/staff-rota/publish", {
        week_start: weekStart,
        week_end: weekEnd,
        branch_id: branchScope && branchScope !== "all" ? branchScope : null,
      });
      toast.success(isAr ? "تم نشر الجدول" : "Rota published");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const openCell = (emp, dayIso, shift) => {
    const isOverride = shift?.source === "override" && shift?.shift_id;
    setModal({
      employee_id: emp.employee_id,
      employee_name: emp.employee_name,
      date: dayIso,
      shift_id: isOverride ? shift.shift_id : null,
      source: shift?.source || "template",
      override_type: shift?.override_type === "off" ? "off" : (shift?.override_type || "work"),
      start: shift?.start || "10:00",
      end: shift?.end || "19:00",
      reason: shift?.reason || "",
      status: "confirmed",
    });
  };

  const saveOverride = async () => {
    if (!modal) return;
    setSaving(true);
    try {
      const payload = {
        employee_id: modal.employee_id,
        date: modal.date,
        override_type: modal.override_type,
        start: modal.override_type === "off" ? null : modal.start,
        end: modal.override_type === "off" ? null : modal.end,
        reason: modal.reason,
        status: modal.status || "confirmed",
      };
      if (modal.shift_id) {
        await api.put(`/staff-rota/overrides/${modal.shift_id}`, payload);
        toast.success(isAr ? "تم تحديث التعديل" : "Override updated");
      } else {
        await api.post("/staff-rota/overrides", payload);
        toast.success(isAr ? "تم حفظ التعديل" : "Override saved");
      }
      setModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const revertOverride = async () => {
    if (!modal?.shift_id) return;
    const ok = await confirm({
      title: isAr
        ? "إزالة التعديل اليومي والعودة للقالب الأسبوعي؟"
        : "Remove this day override and revert to the weekly template?",
      confirmLabel: isAr ? "تأكيد" : "Confirm",
      cancelLabel: isAr ? "إلغاء" : "Cancel",
      variant: "primary",
    });
    if (!ok) return;
    setSaving(true);
    try {
      await api.delete(`/staff-rota/overrides/${modal.shift_id}`);
      toast.success(isAr ? "تمت العودة للقالب" : "Reverted to template");
      setModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const quickOff = async (emp, dayIso, shift) => {
    if (shift?.source === "override" && shift?.shift_id) {
      openCell(emp, dayIso, shift);
      return;
    }
    const ok = await confirm({
      title: isAr
        ? `تعيين إجازة لـ ${emp.employee_name} في ${dayIso}؟`
        : `Mark ${emp.employee_name} off on ${dayIso}?`,
      confirmLabel: isAr ? "تأكيد" : "Confirm",
      cancelLabel: isAr ? "إلغاء" : "Cancel",
      variant: "primary",
    });
    if (!ok) return;
    try {
      await api.post("/staff-rota/overrides", {
        employee_id: emp.employee_id,
        date: dayIso,
        override_type: "off",
        reason: isAr ? "يوم إجازة" : "Day off",
        status: "confirmed",
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const days = data?.days || [];

  return (
    <div className="space-y-5" data-testid="staff-rota-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-heading gold-text">{t("staff_rota")}</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">{t("staff_rota_help")}</p>
          <p className="text-xs text-[var(--text-muted)] mt-2 max-w-3xl">{t("staff_rota_precedence")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="input-dark text-sm min-w-[140px]"
            value={branchScope}
            onChange={(e) => setBranchScope(e.target.value)}
            data-testid="rota-branch-filter"
          >
            <option value="all">{isAr ? "كل الفروع" : "All branches"}</option>
            {(branches || []).map((b) => (
              <option key={b.branch_id} value={b.branch_id}>{isAr ? b.name_ar : b.name_en}</option>
            ))}
          </select>
          <button type="button" onClick={() => setWeekStart(addDays(weekStart, -7))} className="p-2 rounded border border-[var(--border)]">
            <ChevronLeft size={16}/>
          </button>
          <span className="text-sm num-display px-2">{weekStart} → {weekEnd}</span>
          <button type="button" onClick={() => setWeekStart(addDays(weekStart, 7))} className="p-2 rounded border border-[var(--border)]">
            <ChevronRight size={16}/>
          </button>
          <button type="button" onClick={load} disabled={loading} className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2">
            {loading ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
            {t("refresh")}
          </button>
          <button type="button" onClick={publish} className="btn-gold px-4 py-2 rounded-md text-sm flex items-center gap-2" data-testid="publish-rota">
            <Send size={14}/>{isAr ? "نشر الأسبوع" : "Publish week"}
          </button>
        </div>
      </div>

      {data?.published && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2 flex items-center gap-2">
          <Calendar size={14}/>{isAr ? "هذا الأسبوع منشور" : "This week is published"}
        </div>
      )}

      <div className="flex flex-wrap gap-3 text-[10px]">
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 text-emerald-900 border border-emerald-100">
          {isAr ? "أخضر = قالب أسبوعي (من ملف الموظف)" : "Green = weekly template (from employee profile)"}
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-violet-50 text-violet-900 border border-violet-100">
          {isAr ? "بنفسجي/كهرماني = تعديل يومي (يُلغي القالب)" : "Violet/amber = day override (wins over template)"}
        </span>
      </div>

      <RotaTemplatePanel
        employees={data?.employees || []}
        branchId={branchScope && branchScope !== "all" ? branchScope : activeBranchId}
        isAr={isAr}
        t={t}
        onApplied={load}
      />

      <div className="card-surface overflow-auto">
        <table className="table-dark min-w-full text-xs">
          <thead>
            <tr>
              <th className="sticky start-0 bg-[var(--surface)] z-10 min-w-[140px]">{isAr ? "الموظف" : "Employee"}</th>
              {days.map((d) => {
                const dow = new Date(`${d}T12:00:00`).getUTCDay();
                const label = ROTA_DAYS.find((x) => x.k === ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"][dow]);
                return (
                  <th key={d} className="text-center min-w-[88px]">
                    <div>{label ? (isAr ? label.ar : label.en) : ""}</div>
                    <div className="text-[10px] text-[var(--text-muted)] num-display">{d.slice(5)}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {(data?.employees || []).map((emp) => (
              <tr key={emp.employee_id}>
                <td className="sticky start-0 bg-[var(--surface)] z-10 font-medium">
                  <div>{emp.employee_name}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">{emp.employee_code}</div>
                  {!emp.has_weekly_rota && (
                    <div className="text-[9px] text-amber-600 flex items-center gap-0.5 mt-0.5">
                      <AlertTriangle size={9}/>{isAr ? "بدون قالب" : "No template"}
                    </div>
                  )}
                </td>
                {days.map((d) => {
                  const shift = emp.cells?.[d];
                  return (
                    <td key={d} className="p-1">
                      <button
                        type="button"
                        onClick={() => openCell(emp, d, shift)}
                        onContextMenu={(e) => { e.preventDefault(); quickOff(emp, d, shift); }}
                        className={`w-full rounded px-1 py-2 text-center border border-transparent hover:border-[var(--gold)] transition ${cellTone(shift)}`}
                        title={isAr ? "انقر للتعديل · كليك يمين = إجازة" : "Click to edit · right-click = day off"}
                      >
                        <div className="font-mono num-display">{fmtCell(shift)}</div>
                        {shift && (
                          <div className="text-[8px] opacity-70 uppercase tracking-wide">{cellSourceLabel(shift, isAr)}</div>
                        )}
                        {shift?.published && <div className="text-[8px] opacity-70">✓</div>}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
            {(!data?.employees || data.employees.length === 0) && !loading && (
              <tr><td colSpan={8} className="text-center py-12 text-[var(--text-muted)]">{isAr ? "لا يوجد موظفون" : "No employees"}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setModal(null))}>
          <div className="card-surface p-5 w-full max-w-md space-y-4" onMouseDown={onModalPanelMouseDown}>
            <h3 className="font-heading text-lg gold-text">{modal.employee_name}</h3>
            <p className="text-sm text-[var(--text-muted)] num-display">{modal.date}</p>
            {modal.source === "override" && modal.shift_id ? (
              <p className="text-xs text-violet-700 bg-violet-50 border border-violet-100 rounded px-2 py-1">
                {isAr ? "تعديل يومي نشط — يمكنك التعديل أو العودة للقالب" : "Active day override — edit below or revert to template"}
              </p>
            ) : (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-1">
                {isAr ? "القالب الأسبوعي — الحفظ هنا يُنشئ تعديلاً لهذا اليوم فقط" : "Weekly template — saving here creates a one-day override"}
              </p>
            )}
            <div>
              <label className="text-xs text-[var(--text-muted)]">{isAr ? "النوع" : "Type"}</label>
              <select className="input-dark w-full" value={modal.override_type} onChange={(e) => setModal({ ...modal, override_type: e.target.value })}>
                <option value="work">{isAr ? "دوام" : "Work"}</option>
                <option value="off">{isAr ? "إجازة" : "Off"}</option>
                <option value="extra">{isAr ? "ساعات إضافية" : "Extra hours"}</option>
                <option value="emergency_cover">{isAr ? "تغطية طارئة" : "Emergency cover"}</option>
              </select>
            </div>
            {modal.override_type !== "off" && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-[var(--text-muted)]">{isAr ? "من" : "Start"}</label>
                  <input type="time" className="input-dark w-full" value={modal.start} onChange={(e) => setModal({ ...modal, start: e.target.value })}/>
                </div>
                <div>
                  <label className="text-xs text-[var(--text-muted)]">{isAr ? "إلى" : "End"}</label>
                  <input type="time" className="input-dark w-full" value={modal.end} onChange={(e) => setModal({ ...modal, end: e.target.value })}/>
                </div>
              </div>
            )}
            <div>
              <label className="text-xs text-[var(--text-muted)]">{isAr ? "سبب" : "Reason"}</label>
              <input className="input-dark w-full" value={modal.reason} onChange={(e) => setModal({ ...modal, reason: e.target.value })}/>
            </div>
            <div className="flex flex-wrap gap-2 justify-between">
              <div className="flex gap-2">
                {modal.shift_id && (
                  <button
                    type="button"
                    onClick={revertOverride}
                    disabled={saving}
                    className="px-4 py-2 rounded border border-rose-200 text-rose-700 text-sm"
                    data-testid="revert-rota-override"
                  >
                    {isAr ? "العودة للقالب" : "Revert to template"}
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setModal(null)} className="px-4 py-2 rounded border">{t("cancel")}</button>
                <button type="button" onClick={saveOverride} disabled={saving} className="btn-gold px-4 py-2 rounded-md" data-testid="save-rota-override">
                  {saving ? "..." : (modal.shift_id ? (isAr ? "تحديث" : "Update") : (isAr ? "حفظ" : "Save"))}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}

export function WeeklyRotaEditor({ employeeId, lang, branchId }) {
  const isAr = lang === "ar";
  const [rota, setRota] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");

  const normalizeWeeklyRota = useCallback(
    (wr) => normalizeWeeklyRotaPayload(wr, branchId),
    [branchId],
  );

  useEffect(() => {
    api.get("/rota-templates")
      .then((r) => setTemplates(r.data?.templates || []))
      .catch(() => setTemplates([]));
  }, []);

  useEffect(() => {
    if (!employeeId) return;
    setLoading(true);
    api.get(`/employees/${employeeId}/weekly-rota`)
      .then((r) => setRota(normalizeWeeklyRota(r.data.weekly_rota || {})))
      .catch(() => setRota(normalizeWeeklyRota({})))
      .finally(() => setLoading(false));
  }, [employeeId, normalizeWeeklyRota]);

  const setDay = (key, patch) => {
    setRota((prev) => ({
      ...prev,
      [key]: { off: false, start: "10:00", end: "19:00", branch_id: branchId || "", ...(prev[key] || {}), ...patch },
    }));
  };

  const loadFromLibrary = () => {
    const tpl = templates.find((x) => x.template_id === selectedTpl);
    if (!tpl) return;
    setRota(normalizeWeeklyRota(tpl.weekly_rota || {}));
    toast.success(isAr ? "تم تحميل القالب — اضغط حفظ لتطبيقه على الموظف" : "Template loaded — click Save template to apply to this employee");
  };

  const saveToLibrary = async () => {
    const name = window.prompt(isAr ? "اسم القالب في المكتبة:" : "Template name for library:");
    if (!name?.trim()) return;
    const weekly_rota = normalizeWeeklyRota(rota);
    if (countWorkingDays(weekly_rota) === 0) {
      toast.error(isAr ? "يجب أن يكون يوم عمل واحد على الأقل غير محدد كإجازة" : "At least one day must not be marked Off");
      return;
    }
    try {
      await api.post("/rota-templates", { name: name.trim(), weekly_rota, branch_id: branchId || null });
      toast.success(isAr ? "تم حفظ القالب في المكتبة" : "Template saved to library");
      const r = await api.get("/rota-templates");
      setTemplates(r.data?.templates || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const applyFromLibrary = async () => {
    const tpl = templates.find((x) => x.template_id === selectedTpl);
    if (!tpl || !employeeId) return;
    try {
      await api.post(`/rota-templates/${tpl.template_id}/apply`, {
        employee_ids: [employeeId],
        branch_id: branchId || tpl.branch_id || null,
      });
      const r = await api.get(`/employees/${employeeId}/weekly-rota`);
      setRota(normalizeWeeklyRota(r.data.weekly_rota || {}));
      window.dispatchEvent(new CustomEvent("isaloon-rota-template-saved"));
      toast.success(isAr ? "تم تطبيق القالب على الموظف" : "Template applied to employee");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = normalizeWeeklyRota(rota);
      const workingDays = countWorkingDays(payload);
      if (workingDays === 0) {
        toast.error(isAr ? "يجب أن يكون يوم عمل واحد على الأقل غير محدد كإجازة" : "At least one day must not be marked Off");
        return;
      }
      const r = await api.put(`/employees/${employeeId}/weekly-rota`, { weekly_rota: payload });
      const saved = r.data?.weekly_rota || payload;
      setRota(normalizeWeeklyRota(saved));
      window.dispatchEvent(new CustomEvent("isaloon-rota-template-saved"));
      toast.success(
        isAr
          ? `تم حفظ القالب (${r.data?.working_days || workingDays} أيام عمل)`
          : `Template saved (${r.data?.working_days || workingDays} working days)`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  if (!employeeId) return null;
  if (loading) return <div className="text-sm text-[var(--text-muted)] py-4">...</div>;

  return (
    <div className="space-y-3" data-testid="weekly-rota-editor">
      <p className="text-xs text-[var(--text-muted)] bg-[var(--bg)] border border-[var(--border-subtle)] rounded-md p-2">
        {isAr
          ? "اضغط «حفظ القالب» هنا — ليس زر حفظ الموظف الرئيسي. التعديلات اليومية في جدول الدوام تتفوق على هذا القالب."
          : "Click «Save template» here — not the main employee Save button. Day edits on Staff Rota override this template."}
      </p>
      {templates.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <select className="input-dark text-sm min-w-[160px]" value={selectedTpl} onChange={(e) => setSelectedTpl(e.target.value)} data-testid="rota-template-picker">
            <option value="">{isAr ? "— قالب من المكتبة —" : "— Library template —"}</option>
            {templates.map((tpl) => (
              <option key={tpl.template_id} value={tpl.template_id}>
                {isAr && tpl.name_ar ? tpl.name_ar : tpl.name}
              </option>
            ))}
          </select>
          <button type="button" onClick={loadFromLibrary} disabled={!selectedTpl} className="px-3 py-1.5 rounded border text-xs">
            {isAr ? "تحميل" : "Load"}
          </button>
          <button type="button" onClick={applyFromLibrary} disabled={!selectedTpl} className="btn-gold px-3 py-1.5 rounded text-xs">
            {isAr ? "تطبيق مباشر" : "Apply now"}
          </button>
        </div>
      )}
      <RotaWeeklyDayGrid rota={rota} setDay={setDay} isAr={isAr} branchId={branchId}/>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={save} disabled={saving || loading} className="btn-gold px-4 py-2 rounded-md text-sm flex items-center gap-2">
          <Clock size={14}/>{saving ? "..." : (isAr ? "حفظ القالب" : "Save template")}
        </button>
        <button type="button" onClick={saveToLibrary} disabled={saving || loading} className="px-4 py-2 rounded-md text-sm border flex items-center gap-2">
          <BookmarkPlus size={14}/>{isAr ? "حفظ في المكتبة" : "Save to library"}
        </button>
      </div>
    </div>
  );
}
